import axios from 'axios';
import { get, post, del } from './API';

/**
 * A bare axios instance for the presigned upload, with NO interceptors.
 *
 * Services/API.js registers its interceptors on the GLOBAL axios default
 * (`axios.interceptors.request.use`), not on an instance — so calling
 * `axios.put` does NOT bypass them, which is what this file used to assume.
 * They set `Authorization: Bearer <token>` and overwrite `Content-Type` with
 * `application/json` on every request that goes through the default instance.
 *
 * On a presigned S3 URL both are actively harmful:
 *   - the browser then preflights `authorization`, and S3 answers a request
 *     for a header its CORS rule does not list with a 403 carrying no
 *     `Access-Control-Allow-Origin` — which surfaces in the UI as an
 *     unexplained "Network Error", because there is no response to read;
 *   - it would send our dashboard bearer token to a third-party endpoint
 *     that has no business seeing it, on every upload;
 *   - and the forced JSON content type overrides the file's real one.
 *
 * `axios.create()` gets its own empty interceptor chain, so none of that
 * applies. Keep using it here; do not switch back to bare `axios.put`.
 */
const presignedUpload = axios.create();

/**
 * The managed knowledge bases behind the AI assistant's document retrieval.
 *
 * One endpoint, `?action=` for the verbs — the shape PaymentsService already
 * uses, and the reason the query string is built with API.post's 4th argument
 * rather than inlined into the path (`buildUrl` does not parse one).
 *
 * The three scopes a document can carry:
 *   general   — applies to everybody; no counterparty
 *   operator  — this supplier's own paperwork; `counterparty_id` required
 *   partner   — this channel's own paperwork; `counterparty_id` required
 *
 * A retrieval against a counterparty returns THEIR documents and the general
 * ones together, on purpose: "your contract says" and "our standard policy
 * is" are different answers and the caller is expected to show which is which.
 */

export const SCOPES = {
  GENERAL: 'general',
  OPERATOR: 'operator',
  PARTNER: 'partner',
};

export const AUDIENCES = {
  INTERNAL: 'internal',
  CUSTOMER_SAFE: 'customer_safe',
};

/** Human labels, kept next to the values they belong to. */
export const SCOPE_LABELS = {
  [SCOPES.GENERAL]: 'General',
  [SCOPES.OPERATOR]: 'Operator',
  [SCOPES.PARTNER]: 'Partner',
};

export const AUDIENCE_LABELS = {
  [AUDIENCES.INTERNAL]: 'Internal',
  [AUDIENCES.CUSTOMER_SAFE]: 'Customer-safe',
};

/** A scope that names a counterparty cannot be uploaded without one. */
export const scopeNeedsCounterparty = (scope) => (
  scope === SCOPES.OPERATOR || scope === SCOPES.PARTNER
);

export default {
  /** Every knowledge base, with its document count and last ingestion. */
  list: async () => {
    const response = await get('/knowledge-bases');
    return response.data?.data || [];
  },

  /**
   * The documents inside one knowledge base, optionally narrowed to a scope
   * and/or a single counterparty. Empty filters are dropped rather than sent
   * as `?scope=`, which the server would read as a real but empty filter.
   */
  listDocuments: async (kbId, { scope, counterpartyId } = {}) => {
    const params = { action: 'documents', kb_id: kbId };
    if (scope) params.scope = scope;
    if (counterpartyId) params.counterparty_id = counterpartyId;
    const response = await get('/knowledge-bases', params);
    return response.data?.data || [];
  },

  create: async ({ name, description, audience }) => {
    const response = await post('/knowledge-bases', { name, description, audience });
    return response.data?.data || response.data;
  },

  /**
   * Step one of the upload: ask for a presigned PUT. Returns
   * `{ url, key, expires_in }`; the file itself never touches our API.
   */
  requestUploadUrl: async ({
    kbId, filename, contentType, scope, counterpartyId,
  }) => {
    const body = {
      kb_id: kbId,
      filename,
      content_type: contentType,
      scope,
    };
    if (scopeNeedsCounterparty(scope)) body.counterparty_id = counterpartyId;
    const response = await post('/knowledge-bases', body, {}, { action: 'upload-url' });
    return response.data?.data || response.data;
  },

  /**
   * Step two: PUT the bytes straight at S3.
   *
   * A presigned URL carries its own authorisation in the query string, so
   * this must go out with NOTHING of ours attached — see `presignedUpload`
   * above for why that means a dedicated instance rather than `axios.put`.
   */
  uploadToPresignedUrl: async (url, file, onProgress) => presignedUpload.put(url, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    transformRequest: [(data) => data],
    onUploadProgress: onProgress
      ? (event) => {
        if (!event.total) return;
        onProgress(Math.round((event.loaded / event.total) * 100));
      }
      : undefined,
  }),

  /**
   * The raw chunks a query pulls back, with their relevance scores — no model
   * in the loop. This is the "did my document get indexed?" tool; the chat is
   * the "what would an operator actually be told?" one.
   */
  retrieve: async ({
    kbId, query, limit, counterpartyId,
  }) => {
    const body = { kb_id: kbId, query };
    if (limit) body.limit = limit;
    if (counterpartyId) body.counterparty_id = counterpartyId;
    const response = await post('/knowledge-bases', body, {}, { action: 'retrieve' });
    return response.data?.chunks || response.data?.data?.chunks || [];
  },

  /** Kick off a fresh ingestion run. Returns `{ job_id, status }`. */
  reingest: async (kbId) => {
    const response = await post('/knowledge-bases', { kb_id: kbId }, {}, { action: 'reingest' });
    return response.data?.data || response.data;
  },

  /**
   * Delete a whole knowledge base (no `key`) or one document inside it.
   *
   * The identifiers go out in the query string AND the body: every other
   * delete in this app reads them from the query, while the agreed contract
   * for this endpoint writes them as a body. Sending both is cheaper than
   * being wrong about which side the handler reads.
   */
  remove: async (kbId, key) => {
    const payload = key ? { kb_id: kbId, key } : { kb_id: kbId };
    const response = await del('/knowledge-bases', payload, payload);
    return response.data?.data || response.data;
  },
};
